import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { cancel, later, next, once } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { DateRangeInterface, DSData, DSSetup, RequestError, Service } from 'webapp-types';
import { copyObject, utf8_to_b64 } from 'webapp/utils/common';
import { getDateRangeIdFromFormula } from 'webapp/utils/date-range';

import didUpdate from 'webapp/modifiers/did-update';
import type { CompareDisplayData } from 'webapp/components/block/content/common/compares';
import type { MetricInsightsChartState } from 'webapp/components/metric-details/explore/data';
import MetricDetailsTopDatapointsDatapointCardComponent from 'webapp/components/metric-details/top-datapoints/datapoint-card';

import type { MetricSetup } from 'webapp/models-custom';
import type DataSelect from 'webapp/models-custom/data-select';
import type DateRange from 'webapp/models-custom/date-range';
import DsCompare from 'webapp/models-custom/ds-compare';
import DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';

interface Args {
	chartState: MetricInsightsChartState;
	metricSetup: MetricSetup;
	dateRange: DateRange | null;
	forceLoadingState: boolean;
}

export default class MetricDetailsTopDatapointsExplorePreviousPeriodComponent extends Component<Args> {
	@service('model-manager/data-selects')
	declare mmDataSelectsService: Service.ModelManager.DataSelects;
	@service('metric-details') declare metricDetailsService: Service.MetricDetails;
	@service('model-manager/samples') declare mmSamplesService: Service.ModelManager.Samples;
	@service('request') declare requestService: Service.Request;

	private paramHash = '';
	@tracked isLoading = true;
	@tracked dataSelect: DataSelect | null = null;

	private dataQueryXHR: JQuery.jqXHR<unknown> | null = null;
	private retryCount = 0;
	private readonly maxRetries = 6;
	private retryTimer: ReturnType<typeof later> | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		this.recreateDataQuery();
	}

	get isDataLoading() {
		return this.isLoading || this.args.forceLoadingState;
	}

	get currentCompareFormula(): DSSetup.Compare | null {
		const primaryDs = this.args.chartState.block?.blockSetup?.firstShownDataSelect;
		return primaryDs?.limitations.compare?.getFormula() ?? null;
	}

	get blockLastSync(): number {
		const dsData = this.args.chartState.block?.data?.dsData;
		return dsData?.[0]?.lastSync ?? 0;
	}

	@cached
	get points() {
		const points: DChartPrimaryPoint[] = [];
		this.dataSelect?.outputs?.forEach((output) => {
			const p = this.mmSamplesService.createDChartPointsFromOutput(output);
			points.push(...p);
		});
		return points;
	}

	get primaryPoint(): DChartPrimaryPoint | null {
		return this.points[0] || null;
	}

	get comparePoint() {
		return this.primaryPoint?.comparePoints[0] || null;
	}

	get compareValuePoint(): DChartPrimaryPoint | null {
		const cp = this.comparePoint;
		if (!cp) {
			return null;
		}
		return new DChartPrimaryPoint({
			valueStr: cp.valueStr,
			onClick: cp.onClick,
		});
	}

	get primaryComparePoints(): CompareDisplayData[] | undefined {
		return this.primaryPoint?.comparePoints.map((cp) => ({
			...cp,
			indicatorClass: cp.indicatorClass,
			compareText: cp.granulationLabel.labelLong
				? `vs ${cp.granulationLabel.labelLong}`
				: cp.compareText,
		}));
	}

	private setData(
		data: { previewData: DSData.RootTop | null; paramHash: string },
		dataSelectForRetry: DataSelect,
	) {
		if (data.paramHash === this.paramHash) {
			this.dataSelect?.setFakeData(data.previewData);

			const isHorizon = this.args.metricSetup?.metric?.isTypeHorizon;
			const hasCompareData = data.previewData?.compares?.some((c) =>
				c.data?.some((d) => d.items?.length > 0),
			);
			if (isHorizon && !hasCompareData && this.retryCount < this.maxRetries) {
				this.retryCount++;
				this.retryTimer = later(() => {
					if (this.isDestroying || this.isDestroyed) return;
					if (this.paramHash === data.paramHash && !this.comparePoint) {
						this.requestDataQuery(dataSelectForRetry, data.paramHash);
					} else {
						this.isLoading = false;
					}
				}, 5000);
			} else {
				this.isLoading = false;
			}
		}
	}

	private requestDataQuery(dataSelect: DataSelect, paramHash: string) {
		if (!dataSelect.metricSetupKey) return;

		if (this.dataQueryXHR && this.dataQueryXHR.readyState !== 4) {
			this.dataQueryXHR.abort();
		}

		const promise = this.mmDataSelectsService.fetchData(dataSelect, true);

		promise
			.then((previewData: DSData.RootTop) => {
				this.setData({ previewData, paramHash }, dataSelect);
			})
			.catch((errorData: RequestError) => {
				this.setData({ previewData: null, paramHash }, dataSelect);
				this.requestService.handleGenericThrownError(errorData);
			});

		this.dataQueryXHR = (promise.jqPromise as JQuery.jqXHR) ?? null;
	}

	private recreateDataQuery() {
		next(() => {
			next(() => {
				// skip while limitations load — primaryDs isn't updated yet
				// re-fires  when forceLoadingState clears
				if (this.args.forceLoadingState) {
					return;
				}
				const primaryDs = this.args.chartState.block?.blockSetup?.firstShownDataSelect;
				const dataSelectData = primaryDs?.toJSON(true);
				const compareFormula = primaryDs?.limitations.compare?.getFormula();
				const dateRangeFormula = this.args.dateRange?.formula;

				if (dataSelectData && compareFormula && dateRangeFormula) {
					const rangeOnlyFormula: DateRangeInterface.FormulaInterface =
						copyObject(dateRangeFormula);
					delete rangeOnlyFormula.granulation;

					const dateRangeId = getDateRangeIdFromFormula(rangeOnlyFormula);
					const newParamHash = utf8_to_b64(
						[
							JSON.stringify(this.args.metricSetup.metricSetupData),
							dateRangeId,
							JSON.stringify(compareFormula),
						].join(''),
					);

					if (this.paramHash !== newParamHash) {
						this.isLoading = true;
						this.retryCount = 0;
						this.dataSelect?.setFakeData(null);
						this.paramHash = newParamHash;

						dataSelectData.id = this.metricDetailsService.generateMetricInsightsUUID();
						dataSelectData.blockId = null;
						const granulation = dataSelectData.dateRange!.granulation!; // a data select always has a granulation
						granulation.isWholeRange = true;
						granulation.timeUnit = null;
						granulation.multiplier = null;
						delete dataSelectData.compares;

						next(() => {
							const dataSelectModel =
								this.mmDataSelectsService.createNewModel(dataSelectData);
							const dateRange = dataSelectModel.limitations.dateRange;

							if (dateRange && !dateRange.isAllTime && !dateRange.isSince) {
								const newCompare = DsCompare.createFromFormula(compareFormula);
								dataSelectModel.changeCompare(
									dataSelectModel.limitations.compareCMs[0]!,
									newCompare,
								);
							}

							this.requestDataQuery(dataSelectModel, newParamHash);
							this.unloadDataSelect();
							this.dataSelect = dataSelectModel;
						});
					}
				} else {
					this.paramHash = '';
				}
			});
		});
	}

	@action
	refreshDataQuery() {
		once(this, this.recreateDataQuery);
	}

	private unloadDataSelect() {
		if (this.dataSelect) {
			this.dataSelect.model.unloadRecord();
		}
	}

	override willDestroy() {
		super.willDestroy();
		this.dataQueryXHR?.abort();
		if (this.retryTimer) cancel(this.retryTimer);
		this.unloadDataSelect();
	}

	<template>
		<div
			{{didUpdate
				this.refreshDataQuery
				@metricSetup.key
				@metricSetup.dimensions
				@metricSetup.filtersData
				@dateRange.formulaId
				@forceLoadingState
				this.currentCompareFormula
				this.blockLastSync
			}}
		>
			<MetricDetailsTopDatapointsDatapointCardComponent
				@label="vs. previous period"
				@valuePoint={{this.compareValuePoint}}
				@comparePoints={{this.primaryComparePoints}}
				@skipCompareValue={{true}}
				@isLoading={{this.isDataLoading}}
			/>
		</div>
	</template>
}
